import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MAIN_TERMS, PRIVACY_POLICY, BUSINESS_TERMS } from '../../constants/termsContent';

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy' | 'business';
}

export function TermsModal({ isOpen, onClose, type }: TermsModalProps) {
  const { t } = useTranslation();

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getContent = () => {
    switch (type) {
      case 'terms':
        return MAIN_TERMS;
      case 'privacy':
        return PRIVACY_POLICY;
      case 'business':
        return BUSINESS_TERMS;
      default:
        return '';
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'terms':
        return t('businessRegister.termsOfService');
      case 'privacy':
        return t('businessRegister.privacyPolicy');
      case 'business':
        return t('businessRegister.businessTerms');
      default:
        return '';
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-surface-app-elevated rounded-xl w-full max-w-3xl max-h-[90vh] shadow-2xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-strong">
          <h2 id="terms-modal-title" className="text-xl font-semibold text-white">
            {getTitle()}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-ink-secondary hover:text-ink-primary hover:bg-surface-card-hover rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="prose prose-invert prose-sm max-w-none">
            <pre className="whitespace-pre-wrap font-sans text-sm text-ink-primary leading-relaxed">
              {getContent()}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border-strong">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors font-medium"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
